/**
 * Somvera — section reveal.
 *
 * Fades sections in once as they enter the viewport. Deliberately the only
 * bespoke JavaScript in the redesign: everything else (cart, variants, forms,
 * gallery, sticky add to cart) is handled by the theme's own components.
 */

const REVEAL_SELECTOR = '.somvera-reveal';

/** @param {ParentNode} root */
function reveal(root) {
  const targets = root.querySelectorAll(`${REVEAL_SELECTOR}:not(.is-revealed)`);
  if (!targets.length) return;

  // Without IntersectionObserver, or with reduced motion requested, show
  // everything immediately rather than leaving content hidden.
  if (
    !('IntersectionObserver' in window) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    targets.forEach((target) => target.classList.add('is-revealed'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  targets.forEach((target) => observer.observe(target));
}

reveal(document);

// Sections re-rendered by the theme editor need to be picked up again.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) reveal(event.target);
});

/**
 * In-page anchors.
 *
 * The scrolling itself is CSS (`scroll-behavior`), so this only fixes what CSS
 * cannot: a smooth scroll leaves keyboard focus behind at the link, so the next
 * Tab press continues from the top of the page rather than from the section the
 * user just travelled to. Moving focus to the target keeps keyboard and screen
 * reader users in step with the scroll position.
 */
/**
 * Move focus to the section an in-page link just travelled to.
 *
 * The navigation itself is left to the browser. An earlier version called
 * preventDefault and drove the scroll from script, then focused the target
 * straight away — and focusing during a smooth scroll cancels it, so the hash
 * changed while the page stayed still. Waiting for the scroll to finish keeps
 * the CSS smooth scroll intact and still puts keyboard focus in the right
 * place, so the next Tab continues from the new section rather than the top.
 */
function focusAfterScroll(target) {
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');

  let done = false;
  const focusOnce = () => {
    if (done) return;
    done = true;
    target.focus({ preventScroll: true });
  };

  if ('onscrollend' in window) {
    window.addEventListener('scrollend', focusOnce, { once: true });
  }
  // Fallback for browsers without scrollend, and for the case where the target
  // is already in view so no scroll ever starts.
  setTimeout(focusOnce, 700);
}

window.addEventListener('hashchange', () => {
  const id = decodeURIComponent(location.hash.slice(1));
  if (!id) return;
  const target = document.getElementById(id);
  if (target) focusAfterScroll(target);
});
